import type { ReportPayload } from './build.js';

function esc(v: string): string {
  if (v.includes(',') || v.includes('"') || v.includes('\n')) {
    return `"${v.replace(/"/g, '""')}"`;
  }
  return v;
}

export function reportToCsv(data: ReportPayload): string {
  const lines: string[] = [];

  switch (data.reportType) {
    case 'trial_balance':
      lines.push('Code,Name,Type,Debit,Credit');
      for (const r of data.rows) {
        lines.push(
          [r.code, r.name, r.type, r.debitBalance, r.creditBalance].map(esc).join(',')
        );
      }
      lines.push(['', 'Total', '', data.totalDebit, data.totalCredit].map(esc).join(','));
      break;
    case 'general_ledger':
      for (const acc of data.accounts) {
        lines.push(`Account,${esc(acc.code)},${esc(acc.name)}`);
        lines.push('Date,Entry,Memo,Debit,Credit,Balance');
        for (const l of acc.lines) {
          lines.push(
            [l.date, l.entryId, l.memo ?? '', l.debit, l.credit, l.runningBalance]
              .map(esc)
              .join(',')
          );
        }
        lines.push('');
      }
      break;
    case 'profit_loss':
      for (const section of [data.revenue, data.cogs, data.expenses]) {
        lines.push(section.label);
        for (const r of section.rows) {
          lines.push([r.code, r.name, r.amount].map(esc).join(','));
        }
        lines.push(['', 'Subtotal', section.subtotal].map(esc).join(','));
      }
      lines.push(['', 'Gross profit', data.grossProfit].map(esc).join(','));
      lines.push(['', 'Net income', data.netIncome].map(esc).join(','));
      lines.push(['', 'YTD net income', data.ytdNetIncome].map(esc).join(','));
      break;
    case 'balance_sheet':
      for (const section of [data.assets, data.liabilities, data.equity]) {
        lines.push(section.label);
        for (const r of section.rows) {
          lines.push([r.code, r.name, r.amount].map(esc).join(','));
        }
        lines.push(['', 'Subtotal', section.subtotal].map(esc).join(','));
      }
      break;
    case 'cash_flow':
      lines.push(['Net income', data.netIncome].map(esc).join(','));
      lines.push(['Working capital change', data.workingCapitalChange].map(esc).join(','));
      lines.push(['Net cash from operations', data.netCashFromOperations].map(esc).join(','));
      lines.push(['Note', data.footerNote].map(esc).join(','));
      break;
    case 'equity_statement':
      lines.push('Code,Name,Opening,Movement,Closing');
      for (const r of data.rows) {
        lines.push([r.code, r.name, r.opening, r.movement, r.closing].map(esc).join(','));
      }
      lines.push(
        ['', 'Totals', data.openingEquity, data.netIncome, data.closingEquity].map(esc).join(',')
      );
      break;
    case 'journal_register':
      lines.push('ID,Date,Source,Memo,Debit,Credit,Lines');
      for (const r of data.rows) {
        lines.push(
          [r.id, r.date, r.source, r.memo ?? '', r.debitTotal, r.creditTotal, String(r.lineCount)]
            .map(esc)
            .join(',')
        );
      }
      break;
    case 'vat_summary_eg':
    case 'vat_summary_sa':
    case 'vat_summary_gcc':
      lines.push(['Metric', 'Amount'].map(esc).join(','));
      lines.push(['Output VAT', data.outputVat].map(esc).join(','));
      lines.push(['Input VAT', data.inputVat].map(esc).join(','));
      lines.push(['Net payable', data.netPayable].map(esc).join(','));
      lines.push(['Sales invoices', String(data.salesInvoiceCount)].map(esc).join(','));
      lines.push(['Purchase bills', String(data.purchaseBillCount)].map(esc).join(','));
      break;
    case 'ar_aging':
    case 'ap_aging':
      lines.push('Party,Code,Current,30,60,90,Total');
      for (const r of data.rows) {
        lines.push(
          [r.partyName, r.code, r.current, r.days30, r.days60, r.days90, r.total]
            .map(esc)
            .join(',')
        );
      }
      break;
    case 'profit_loss_by_cost_center':
      lines.push('Cost center,Revenue,Expenses,Net');
      for (const r of data.rows) {
        lines.push([r.costCenterName, r.revenue, r.expenses, r.net].map(esc).join(','));
      }
      break;
    case 'fixed_asset_schedule':
      lines.push('Name,Cost,Accum dep,NBV,Status');
      for (const r of data.rows) {
        lines.push(
          [r.name, r.cost, r.accumulatedDepreciation, r.netBookValue, r.status].map(esc).join(',')
        );
      }
      lines.push(['', 'Total', '', data.totalNbv, ''].map(esc).join(','));
      break;
    case 'payroll_summary':
      lines.push('Pay date,Net pay,Status');
      for (const r of data.rows) {
        lines.push([r.payDate, r.netPay, r.status].map(esc).join(','));
      }
      lines.push(['', data.totalNet, ''].map(esc).join(','));
      break;
    case 'e_invoice_register':
      lines.push('Number,Date,UUID,Gross,Tax,Format');
      for (const r of data.rows) {
        lines.push(
          [r.documentNumber, r.date, r.eInvoiceUuid, r.grossTotal, r.taxTotal, r.format]
            .map(esc)
            .join(',')
        );
      }
      break;
    case 'bank_reconciliation':
      lines.push('Code,Name,GL balance,Unreconciled count,Unreconciled total');
      for (const a of data.accounts) {
        lines.push(
          [a.code, a.name, a.glBalance, String(a.unreconciledCount), a.unreconciledTotal]
            .map(esc)
            .join(',')
        );
      }
      break;
    case 'inventory_valuation':
      lines.push('Code,Name,Qty,Unit cost,Value');
      for (const r of data.rows) {
        lines.push([r.code, r.name, r.qty, r.unitCost, r.value].map(esc).join(','));
      }
      lines.push(['', 'Total', '', '', data.totalValue].map(esc).join(','));
      break;
    case 'customer_statement':
    case 'vendor_statement':
      lines.push('Date,Reference,Debit,Credit,Balance');
      for (const r of data.rows) {
        lines.push([r.date, r.reference, r.debit, r.credit, r.balance].map(esc).join(','));
      }
      lines.push(['', 'Closing', '', '', data.closingBalance].map(esc).join(','));
      break;
    case 'audit_export':
      lines.push('At,Action,User,Detail');
      for (const r of data.rows) {
        lines.push([r.at, r.action, r.userId, r.detail].map(esc).join(','));
      }
      break;
    case 'withholding_summary':
      lines.push('Date,Reference,Vendor,Amount');
      for (const r of data.rows) {
        lines.push([r.date, r.reference, r.vendorName, r.amount].map(esc).join(','));
      }
      lines.push(['', '', 'Total', data.totalWithheld].map(esc).join(','));
      break;
    case 'tax_reconciliation':
      lines.push('Metric,Subledger,GL,Variance');
      lines.push(['Output VAT', data.outputVatSubledger, data.outputVatGl, data.outputVariance].map(esc).join(','));
      lines.push(['Input VAT', data.inputVatSubledger, data.inputVatGl, data.inputVariance].map(esc).join(','));
      lines.push(['Net payable', data.netPayableSubledger, data.netPayableGl, data.netVariance].map(esc).join(','));
      break;
    case 'budget_vs_actual':
      lines.push('Code,Name,Budget,Actual,Variance');
      for (const r of data.rows) {
        lines.push([r.code, r.name, r.budget, r.actual, r.variance].map(esc).join(','));
      }
      lines.push(['', 'Total', data.totalBudget, data.totalActual, data.totalVariance].map(esc).join(','));
      break;
    case 'depreciation_report':
      lines.push('Date,Entry,Memo,Amount');
      for (const r of data.rows) {
        lines.push([r.date, r.entryId, r.memo, r.amount].map(esc).join(','));
      }
      lines.push(['', '', 'Total', data.totalDepreciation].map(esc).join(','));
      break;
    case 'sales_register':
      lines.push('Number,Date,Customer,Net,Tax,Gross,Paid,Balance,Status');
      for (const r of data.rows) {
        lines.push(
          [
            r.documentNumber,
            r.date,
            r.customerName,
            r.netTotal,
            r.taxTotal,
            r.grossTotal,
            r.amountPaid,
            r.balanceDue,
            r.status
          ]
            .map(esc)
            .join(',')
        );
      }
      lines.push(['', '', 'Totals', '', data.totalTax, data.totalGross, data.totalPaid, '', ''].map(esc).join(','));
      break;
    case 'purchase_register':
      lines.push('Number,Date,Vendor,Net,Tax,Gross,Withholding,Paid,Balance,Status');
      for (const r of data.rows) {
        lines.push(
          [
            r.documentNumber,
            r.date,
            r.vendorName,
            r.netTotal,
            r.taxTotal,
            r.grossTotal,
            r.withholdingTotal,
            r.amountPaid,
            r.balanceDue,
            r.status
          ]
            .map(esc)
            .join(',')
        );
      }
      lines.push(
        ['', '', 'Totals', '', data.totalTax, data.totalGross, data.totalWithholding, '', '', '']
          .map(esc)
          .join(',')
      );
      break;
  }

  return lines.join('\n');
}
